import React, { FormEvent, useEffect, useId, useState } from "react";
import {
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import { Puff } from "react-loader-spinner";
import Breadcrumbs from "components/Common/Breadcrumb";
import CustomGooglePlacesAutocomplete from "components/Common/auto-complete";
import { ConnectedStoreAddress } from "models/ConnectedStore";
import { environment } from "environment/environment";
import { StoreConnectService } from "services/StoreConnectService";
import { geocodeByPlaceId } from "react-google-places-autocomplete";
import { toast } from "react-toastify";
import {
  ShopifyStoreLogo,
  SquareStoreLogo,
  WooCommerceStoreLogo,
} from "./platformLogos";
import "./CustomerStores.css";

const emptyPickup: ConnectedStoreAddress = {
  street: "",
  unit: "",
  city: "",
  postalCode: "",
};

const addressSearchOptions = {
  componentRestrictions: { country: "ca" },
  locationRestriction: {
    north: 56.85,
    south: 41.6,
    east: -74.0,
    west: -95.15,
  },
};

const SHOP_HANDLE_PATTERN = /^[a-z0-9][a-z0-9-]*$/;
const SHOP_DOMAIN_PATTERN = /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/;

type PlatformId = "shopify" | "woocommerce" | "square";

type PlatformTile = {
  id: PlatformId;
  available: boolean;
  title: string;
  subtitle: string;
  Logo: React.FC<{ className?: string }>;
};

const PLATFORM_TILES: PlatformTile[] = [
  {
    id: "shopify",
    available: true,
    title: "Connect Shopify",
    subtitle: "Install Zulu plugin",
    Logo: ShopifyStoreLogo,
  },
  {
    id: "woocommerce",
    available: false,
    title: "WooCommerce",
    subtitle: "Coming soon",
    Logo: WooCommerceStoreLogo,
  },
  {
    id: "square",
    available: false,
    title: "Square",
    subtitle: "Coming soon",
    Logo: SquareStoreLogo,
  },
];

/** Normalize a shop handle or full *.myshopify.com domain for the install URL. */
function normalizeShopDomain(raw: string): string | null {
  let value = String(raw || "").trim().toLowerCase();
  if (!value) {
    return null;
  }
  value = value.replace(/^https?:\/\//, "").replace(/\/.*$/, "");
  if (value.endsWith(".myshopify.com")) {
    return SHOP_DOMAIN_PATTERN.test(value) ? value : null;
  }
  if (!SHOP_HANDLE_PATTERN.test(value)) {
    return null;
  }
  return `${value}.myshopify.com`;
}

function shopifyAdminAppUrl(shop: string): string {
  const base = String(environment.api_url || "").replace(/\/$/, "");
  return `${base}/api/v1/integrations/shopify/open?shop=${encodeURIComponent(shop)}`;
}

const CustomerStores: React.FC = () => {
  const shopInputId = useId();

  const [modalOpen, setModalOpen] = useState(false);
  const [shopHandle, setShopHandle] = useState("");
  const [error, setError] = useState("");
  const [redirecting, setRedirecting] = useState(false);
  const [hasCustomStore, setHasCustomStore] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  const [joining, setJoining] = useState(false);
  const [storeName, setStoreName] = useState("");
  const [pickup, setPickup] = useState<ConnectedStoreAddress>(emptyPickup);

  useEffect(() => {
    document.title = "Store | Zulu Courier";
    StoreConnectService.getStatus()
      .then(res => {
        if (res.success && res.data) {
          setHasCustomStore(Boolean(res.data.hasCustomStore));
        }
      })
      .catch(() => undefined);
  }, []);

  const openConnect = () => {
    setError("");
    setShopHandle("");
    setRedirecting(false);
    setModalOpen(true);
  };

  const closeConnect = () => {
    if (redirecting) {
      return;
    }
    setModalOpen(false);
  };

  const startInstall = (event?: FormEvent) => {
    event?.preventDefault();
    const shop = normalizeShopDomain(shopHandle);
    if (!shop) {
      setError("Enter your Shopify store name (example: my-store).");
      return;
    }
    setError("");
    setRedirecting(true);
    window.location.assign(shopifyAdminAppUrl(shop));
  };

  const selectPickup = async (_label: string, placeId: string) => {
    if (!placeId) return;
    try {
      const [place] = await geocodeByPlaceId(placeId);
      const component = (type: string) =>
        place.address_components.find(item => item.types.includes(type))
          ?.long_name || "";
      const street = [component("street_number"), component("route")]
        .filter(Boolean)
        .join(" ");
      const city =
        component("locality") ||
        component("postal_town") ||
        component("administrative_area_level_2");
      setPickup(current => ({
        ...current,
        street: street || current.street,
        city: city || current.city,
        postalCode:
          component("postal_code").toUpperCase() || current.postalCode,
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      }));
    } catch (_error) {
      toast.error("Could not read that pickup address. Please try again.");
    }
  };

  const openOrJoinCustomStore = async () => {
    if (hasCustomStore) {
      setJoining(true);
      try {
        const res = await StoreConnectService.joinStore({
          storeName: storeName.trim() || "My Store",
        });
        if (res.success && res.data?.redirectPath) {
          window.location.assign(res.data.redirectPath);
          return;
        }
        toast.error(res.error || res.message || "Could not open your store.");
      } catch (err: any) {
        toast.error(
          err?.response?.data?.error ||
            err?.message ||
            "Could not open your store."
        );
      } finally {
        setJoining(false);
      }
      return;
    }
    setJoinOpen(true);
  };

  const submitJoin = async () => {
    if (storeName.trim().length < 2) {
      toast.error("Enter a store name.");
      return;
    }
    if (
      !pickup.street?.trim() ||
      !pickup.city?.trim() ||
      !Number.isFinite(Number(pickup.lat)) ||
      !Number.isFinite(Number(pickup.lng))
    ) {
      toast.error("Search and select a default pickup location.");
      return;
    }
    setJoining(true);
    try {
      const res = await StoreConnectService.joinStore({
        storeName: storeName.trim(),
        defaultDeliveryMethod: "drop_at_door",
        defaultPickupAddress: pickup,
      });
      if (res.success && res.data?.redirectPath) {
        toast.success(
          res.data.alreadyLinked
            ? "Opening your store dashboard…"
            : "Your store was added to Zulu."
        );
        window.location.assign(res.data.redirectPath);
        return;
      }
      toast.error(res.error || res.message || "Could not add your store.");
    } catch (err: any) {
      toast.error(
        err?.response?.data?.error ||
          err?.message ||
          "Could not add your store."
      );
    } finally {
      setJoining(false);
    }
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Store" breadcrumbItem="Connect store" />

        <div className="customer-stores__intro">
          <h4>Connect a store</h4>
          <p>
            Connect your ecommerce platform to sync orders and offer Zulu
            delivery at checkout — or add your existing shop directly to Zulu.
          </p>
        </div>

        <div className="customer-stores__grid">
          {PLATFORM_TILES.map((tile) => {
            const { Logo } = tile;
            if (!tile.available) {
              return (
                <div
                  key={tile.id}
                  className="customer-stores__connect is-disabled"
                  aria-disabled="true"
                  aria-label={`${tile.title} — Coming soon`}
                >
                  <span className="customer-stores__soon">Coming soon</span>
                  <span className="customer-stores__logo-wrap" aria-hidden="true">
                    <Logo className="customer-stores__logo" />
                  </span>
                  <span className="customer-stores__connect-label">
                    <strong>{tile.title}</strong>
                    <span>{tile.subtitle}</span>
                  </span>
                </div>
              );
            }
            return (
              <button
                key={tile.id}
                type="button"
                className="customer-stores__connect"
                onClick={openConnect}
                aria-label={`${tile.title} — ${tile.subtitle}`}
              >
                <span className="customer-stores__logo-wrap" aria-hidden="true">
                  <Logo className="customer-stores__logo" />
                </span>
                <span className="customer-stores__connect-label">
                  <strong>{tile.title}</strong>
                  <span>{tile.subtitle}</span>
                </span>
              </button>
            );
          })}

          <button
            type="button"
            className="customer-stores__connect is-zulu"
            disabled={joining}
            onClick={openOrJoinCustomStore}
            aria-label={
              hasCustomStore
                ? "Open your Zulu store"
                : "Add your store to Zulu"
            }
          >
            <span className="customer-stores__logo-wrap is-zulu" aria-hidden="true">
              <i className="bx bx-store" />
            </span>
            <span className="customer-stores__connect-label">
              <strong>
                {hasCustomStore
                  ? "Open your Zulu store"
                  : "Add your store to Zulu"}
              </strong>
              <span>
                {hasCustomStore
                  ? "Go to your merchant dashboard"
                  : "For existing shop owners"}
              </span>
            </span>
          </button>
        </div>

        <Modal isOpen={modalOpen} toggle={closeConnect} centered>
          <ModalHeader toggle={closeConnect}>Connect Shopify</ModalHeader>
          <ModalBody>
            <p className="customer-stores__modal-copy">
              Enter your Shopify store name. We&apos;ll open the Zulu app in
              Shopify Admin. Shopify asks you to install it if it is not on
              that store yet.
            </p>
            <form onSubmit={startInstall}>
              <label className="form-label" htmlFor={shopInputId}>
                Shopify store
              </label>
              <div className="customer-stores__shop-field">
                <input
                  id={shopInputId}
                  type="text"
                  autoComplete="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  placeholder="my-store"
                  value={shopHandle}
                  disabled={redirecting}
                  onChange={(event) => {
                    setShopHandle(event.target.value);
                    if (error) {
                      setError("");
                    }
                  }}
                  aria-invalid={Boolean(error)}
                  aria-describedby={error ? `${shopInputId}-error` : undefined}
                />
                <span className="customer-stores__suffix">.myshopify.com</span>
              </div>
              {error ? (
                <p
                  id={`${shopInputId}-error`}
                  className="customer-stores__error"
                  role="alert"
                >
                  {error}
                </p>
              ) : null}
              <div className="customer-stores__actions">
                <Button
                  type="button"
                  color="secondary"
                  outline
                  onClick={closeConnect}
                  disabled={redirecting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={redirecting || !shopHandle.trim()}
                >
                  {redirecting ? "Opening Shopify…" : "Open in Shopify"}
                </Button>
              </div>
            </form>
          </ModalBody>
        </Modal>

        <Modal
          isOpen={joinOpen}
          toggle={() => !joining && setJoinOpen(false)}
          centered
          size="lg"
        >
          <ModalHeader toggle={() => !joining && setJoinOpen(false)}>
            Add your store to Zulu
          </ModalHeader>
          <ModalBody>
            <p className="customer-stores__modal-copy">
              Use this if you already run a shop and want Zulu delivery without
              installing Shopify or WooCommerce.
            </p>
            <FormGroup>
              <Label for="joinStoreName">Store name</Label>
              <Input
                id="joinStoreName"
                value={storeName}
                maxLength={120}
                disabled={joining}
                onChange={event => setStoreName(event.target.value)}
              />
            </FormGroup>
            <FormGroup className="mb-2">
              <Label>Default pickup location</Label>
              {environment.google_maps_key ? (
                <CustomGooglePlacesAutocomplete
                  apiKey={environment.google_maps_key}
                  autocompletionRequest={addressSearchOptions}
                  addressType="pickup"
                  handleAddress={selectPickup}
                />
              ) : null}
            </FormGroup>
            <Row>
              <Col md={8}>
                <FormGroup>
                  <Label for="joinStreet">Street</Label>
                  <Input
                    id="joinStreet"
                    value={pickup.street || ""}
                    disabled={joining}
                    onChange={event =>
                      setPickup(current => ({
                        ...current,
                        street: event.target.value,
                      }))
                    }
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="joinUnit">Unit</Label>
                  <Input
                    id="joinUnit"
                    value={pickup.unit || ""}
                    disabled={joining}
                    onChange={event =>
                      setPickup(current => ({
                        ...current,
                        unit: event.target.value,
                      }))
                    }
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="joinCity">City</Label>
                  <Input
                    id="joinCity"
                    value={pickup.city || ""}
                    disabled={joining}
                    onChange={event =>
                      setPickup(current => ({
                        ...current,
                        city: event.target.value,
                      }))
                    }
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup className="mb-0">
                  <Label for="joinPostal">Postal code</Label>
                  <Input
                    id="joinPostal"
                    value={pickup.postalCode || ""}
                    disabled={joining}
                    onChange={event =>
                      setPickup(current => ({
                        ...current,
                        postalCode: event.target.value,
                      }))
                    }
                  />
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              outline
              disabled={joining}
              onClick={() => setJoinOpen(false)}
            >
              Cancel
            </Button>
            <Button color="primary" disabled={joining} onClick={submitJoin}>
              {joining ? (
                <Puff color="#FFD0B8" height={18} width={18} wrapperClass="me-2" />
              ) : null}
              Add store
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </div>
  );
};

export default CustomerStores;
